import {
  AddOutlined,
  ArchiveOutlined,
  BusinessOutlined,
  EditOutlined,
  SearchOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Supplier,
  archiveSupplier,
  createSupplier,
  isAuthenticationError,
  loadSuppliers,
  updateSupplier
} from "./api";
import { login } from "./auth";
import { ListRowActions } from "./ListRowActions";
import { matchesListSyntax } from "./listSearch";
import { PageHeader } from "./PageHeader";

const organizationId = "10000000-0000-0000-0000-000000000001";

export function SupplierMasterData({
  onBack,
  permissions
}: {
  onBack: () => void;
  permissions: string[];
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [externalReference, setExternalReference] = useState("");
  const [listTab, setListTab] = useState<"active" | "archived">("active");
  const [syntaxSearch, setSyntaxSearch] = useState("");
  const [editedSupplier, setEditedSupplier] = useState<Supplier | null>(null);
  const [supplierToArchive, setSupplierToArchive] =
    useState<Supplier | null>(null);
  const canManage = permissions.includes("supplier:manage");

  const suppliers = useQuery({
    queryKey: ["suppliers"],
    queryFn: loadSuppliers
  });
  const filteredSuppliers = useMemo(() =>
    (suppliers.data ?? []).filter((supplier) =>
      (supplier.status !== "ACTIVE") === (listTab === "archived")
      && (!syntaxSearch.trim() || matchesListSyntax({
        id: `${supplier.id} ${supplier.code}`,
        libelle: supplier.displayName,
        reference: supplier.externalReference,
        statut: supplier.status
      }, syntaxSearch))
    ), [listTab, suppliers.data, syntaxSearch]);
  const create = useMutation({
    mutationFn: () => createSupplier({
      organizationId,
      code,
      displayName,
      externalReference: externalReference || null
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["suppliers"] });
      setDialogOpen(false);
      setCode("");
      setDisplayName("");
      setExternalReference("");
    }
  });
  const update = useMutation({
    mutationFn: () => updateSupplier(editedSupplier!.id, {
      displayName,
      externalReference: externalReference || null,
      version: editedSupplier!.version
    }),
    onSuccess: async () => {
      setEditedSupplier(null);
      await queryClient.invalidateQueries({ queryKey: ["suppliers"] });
    }
  });
  const archive = useMutation({
    mutationFn: () => archiveSupplier(
      supplierToArchive!.id, supplierToArchive!.version
    ),
    onSuccess: async () => {
      setSupplierToArchive(null);
      await queryClient.invalidateQueries({ queryKey: ["suppliers"] });
    }
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    create.mutate();
  }

  return (
    <Stack spacing={1.5} sx={{
      height: { md: "100%" }, minHeight: 0
    }}>
      <PageHeader
        title={t("masterData.suppliers.title")}
        compact
        onBack={onBack}
        actions={canManage ? (
          <Button variant="contained" startIcon={<AddOutlined />}
            onClick={() => setDialogOpen(true)}>
            {t("masterData.suppliers.new")}
          </Button>
        ) : undefined}
      />

      {suppliers.isError && (
        <Alert severity="error"
          action={isAuthenticationError(suppliers.error)
            ? <Button color="inherit" size="small"
                onClick={() => void login()}>
                {t("masterData.common.reconnect")}
              </Button>
            : <Button color="inherit" size="small"
                onClick={() => void suppliers.refetch()}>
                {t("masterData.common.retry")}
              </Button>}>
          {t("masterData.suppliers.loadError")}
        </Alert>
      )}
      {!suppliers.isLoading && !suppliers.isError
        && suppliers.data?.length === 0 && (
          <Alert severity="info">
            {t("masterData.suppliers.first")}
          </Alert>
        )}

      <Card variant="outlined">
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <TextField fullWidth label={t("masterData.common.search")}
            placeholder={'id:SUP001 libelle:"fournisseur" reference:ERP'}
            value={syntaxSearch}
            onChange={(event) => setSyntaxSearch(event.target.value)}
            InputProps={{ startAdornment:
              <SearchOutlined color="action" sx={{ mr: 1 }} /> }} />
        </CardContent>
      </Card>
      <Tabs value={listTab}
          onChange={(_, value: "active" | "archived") => setListTab(value)}>
          <Tab value="active" label={`${t("masterData.common.active")} (${suppliers.data?.filter(
            (supplier) => supplier.status === "ACTIVE").length ?? 0})`} />
          <Tab value="archived" label={`${t("masterData.common.archived")} (${suppliers.data?.filter(
            (supplier) => supplier.status !== "ACTIVE").length ?? 0})`} />
      </Tabs>
      <Box sx={{
        minHeight: 0, flex: { md: 1 }, overflowY: { md: "auto" },
        pr: { md: .5 }
      }}>
      <Stack spacing={1.5}>
        {(suppliers.data?.length ?? 0) > 0
          && filteredSuppliers.length === 0 && (
            <Alert severity="info">
              {t("masterData.common.noSearchResult")}
            </Alert>
          )}
        {filteredSuppliers.map((supplier) => (
          <Card key={supplier.id} variant="outlined">
            <CardContent>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}
                alignItems={{ sm: "center" }}>
                <BusinessOutlined color="secondary" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={800}>
                    {supplier.code} · {supplier.displayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("masterData.suppliers.erpReferenceValue", { value: supplier.externalReference ?? t("masterData.common.notDefined") })}
                  </Typography>
                </Box>
                <ListRowActions actions={
                  canManage && supplier.status === "ACTIVE" ? (
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" startIcon={<EditOutlined />}
                        onClick={() => {
                          setDisplayName(supplier.displayName);
                          setExternalReference(
                            supplier.externalReference ?? ""
                          );
                          setEditedSupplier(supplier);
                        }}>
                        {t("masterData.common.edit")}
                      </Button>
                      <Button variant="outlined" color="error"
                        startIcon={<ArchiveOutlined />}
                        onClick={() => setSupplierToArchive(supplier)}>
                        {t("masterData.common.archive")}
                      </Button>
                    </Stack>
                  ) : undefined
                } status={
                  <Chip label={t(`masterData.common.statuses.${supplier.status}`, { defaultValue: supplier.status })}
                    color={supplier.status === "ACTIVE"
                      ? "success" : "default"} />
                } />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}
        fullWidth maxWidth="sm">
        <Box component="form" onSubmit={submit}>
          <DialogTitle>{t("masterData.suppliers.create")}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField label={t("masterData.suppliers.code")} required value={code}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                inputProps={{ maxLength: 50 }} />
              <TextField label={t("masterData.common.name")} required value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                inputProps={{ maxLength: 200 }} />
              <TextField label={t("masterData.suppliers.erpReference")} value={externalReference}
                onChange={(event) => setExternalReference(event.target.value)}
                inputProps={{ maxLength: 100 }} />
              {create.isError && (
                <Alert severity="error">
                  {t("masterData.suppliers.createError")}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>{t("masterData.common.cancel")}</Button>
            <Button type="submit" variant="contained"
              disabled={create.isPending}>
              {t("masterData.common.create")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Dialog open={editedSupplier !== null}
        onClose={() => setEditedSupplier(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t("masterData.suppliers.editTitle", { code: editedSupplier?.code })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label={t("masterData.suppliers.code")} value={editedSupplier?.code ?? ""}
              disabled helperText={t("masterData.common.masterCodeImmutable")} />
            <TextField label={t("masterData.common.name")} required value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              inputProps={{ maxLength: 200 }} />
            <TextField label={t("masterData.suppliers.erpReference")} value={externalReference}
              onChange={(event) => setExternalReference(event.target.value)}
              inputProps={{ maxLength: 100 }} />
            {update.isError && (
              <Alert severity="error">
                {t("masterData.suppliers.editError")}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditedSupplier(null)}>{t("masterData.common.cancel")}</Button>
          <Button variant="contained" disabled={!displayName.trim()
            || update.isPending} onClick={() => update.mutate()}>
            {t("masterData.common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={supplierToArchive !== null}
        onClose={() => setSupplierToArchive(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {t("masterData.suppliers.archiveTitle", { code: supplierToArchive?.code })}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            {t("masterData.suppliers.archiveHelp")}
          </Alert>
          {archive.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t("masterData.suppliers.archiveError")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupplierToArchive(null)}>{t("masterData.common.cancel")}</Button>
          <Button variant="contained" color="error"
            disabled={archive.isPending} onClick={() => archive.mutate()}>
            {t("masterData.common.archive")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
